import styled, { css } from 'styled-components';

import { StyledTextProps, textStyles } from '../../styles';
import { ValueOf } from '../../types';

import { ListItemVariableNames } from './constants';

type ListItemStyledProps = {
  $hasTransparentBackground: boolean;
  $isHighlighted?: boolean;
};

const variableStyles = css<ListItemStyledProps>(
  ({
    theme,
    $hasTransparentBackground,
  }): Record<ValueOf<typeof ListItemVariableNames>, string | undefined> => {
    const variant = theme.components.ListItem;

    return {
      [ListItemVariableNames.BackgroundColor]: $hasTransparentBackground
        ? 'transparent'
        : variant['bg-default'],
      [ListItemVariableNames.HoverBackgroundColor]: variant['bg-hover'],
      [ListItemVariableNames.FocusBorderColor]: variant.shadow,
      [ListItemVariableNames.FontSize]: variant['font-size'],
      [ListItemVariableNames.BorderSize]: variant['border-size'],
      [ListItemVariableNames.HorizontalPadding]: variant['horizontal-padding'],
      [ListItemVariableNames.Color]: variant['fg-default'],
      [ListItemVariableNames.IconColor]: variant['icon-default'],
      [ListItemVariableNames.IconSize]: variant['icon-size'],
      [ListItemVariableNames.Gap]: variant.inside,
      [ListItemVariableNames.Height]: variant.height,
    };
  },
);

const clickableStyles = css`
  cursor: pointer;

  &:focus-visible,
  &:hover {
    background-color: var(${ListItemVariableNames.HoverBackgroundColor});
  }

  &:focus-visible {
    outline: none;
    border: var(${ListItemVariableNames.BorderSize}) solid
      var(${ListItemVariableNames.FocusBorderColor});
  }
`;

const iconStyles = css`
  width: var(${ListItemVariableNames.IconSize});
  height: var(${ListItemVariableNames.IconSize});
  fill: var(${ListItemVariableNames.IconColor});
`;

export const ListItemRoot = styled.li<ListItemStyledProps>`
  ${variableStyles}

  white-space: nowrap;
  text-overflow: ellipsis;

  user-select: none;
  box-sizing: border-box;
  display: flex;
  gap: var(${ListItemVariableNames.Gap});
  align-items: center;
  width: 100%;
  padding: 0
    calc(
      var(${ListItemVariableNames.HorizontalPadding}) - var(
          ${ListItemVariableNames.BorderSize}
        )
    );

  border: var(${ListItemVariableNames.BorderSize}) solid transparent;
  color: var(${ListItemVariableNames.Color});
  background-color: var(${ListItemVariableNames.BackgroundColor});
  min-height: var(${ListItemVariableNames.Height});

  ${({ onClick }) => (onClick ? clickableStyles : '')}

  ${({ $isHighlighted }) =>
    $isHighlighted
      ? `background-color: var(${ListItemVariableNames.HoverBackgroundColor});`
      : ''}

  &[aria-disabled="true"] {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

export const LeftContainer = styled.div`
  display: flex;
  flex: 0 0 auto;
  align-items: center;

  & > svg {
    ${iconStyles}
  }

  &:empty {
    display: none;
  }
`;

export const RightContainer = styled.div`
  display: flex;
  flex: 0 0 auto;
  align-items: center;

  & > svg {
    ${iconStyles}
  }

  &:empty {
    display: none;
  }
`;

export const ChildContainer = styled.div<StyledTextProps>`
  display: flex;
  align-items: center;
  flex-grow: 1;
  flex-shrink: 1;
  overflow: hidden;
  font-size: var(${ListItemVariableNames.FontSize});
  line-height: var(${ListItemVariableNames.IconSize});
  text-overflow: ellipsis;
  white-space: nowrap;

  ${textStyles}
`;
